import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import type { ModelSelection, ProviderKind, ServerProvider } from "@cmmd-forge/contracts";
import { useCallback } from "react";
import { resolveAppModelSelection } from "../../modelSelection";
import { resolveSelectableProvider } from "../../providerModels";
import { type Thread } from "../../types";
import { type UnifiedSettings } from "@cmmd-forge/contracts/settings";
import { resolveProviderAccountIdentity } from "@cmmd-forge/shared/providerAccountIdentity";
import { useProviderUsageRead } from "~/usage/useProviderUsage";

export function useProviderModelSelectionControls(input: {
  readonly activeThread: Thread | undefined;
  readonly currentModelSelection?: ModelSelection | undefined;
  readonly lockedProvider: ProviderKind | null;
  readonly providerStatuses: readonly ServerProvider[];
  readonly settings: UnifiedSettings;
  readonly scheduleComposerFocus: () => void;
  readonly setComposerDraftModelSelection: (
    target: ReturnType<typeof scopeThreadRef>,
    modelSelection: ModelSelection | null | undefined,
  ) => void;
  readonly setStickyComposerModelSelection: (
    modelSelection: ModelSelection | null | undefined,
  ) => void;
}): {
  readonly onProviderModelSelect: (provider: ProviderKind, model: string) => void;
} {
  const providerUsage = useProviderUsageRead();
  const onProviderModelSelect = useCallback(
    (provider: ProviderKind, model: string) => {
      if (input.lockedProvider !== null && provider !== input.lockedProvider) {
        input.scheduleComposerFocus();
        return;
      }
      const resolvedProvider = resolveSelectableProvider(input.providerStatuses, provider);
      const resolvedModel = resolveAppModelSelection(
        resolvedProvider,
        input.settings,
        input.providerStatuses,
        model,
      );
      const selectedProviderAccountId =
        input.currentModelSelection?.provider === resolvedProvider &&
        "providerAccountId" in input.currentModelSelection
          ? input.currentModelSelection.providerAccountId
          : undefined;
      const providerAccounts = providerUsage.accounts
        .map(({ account }) => account)
        .filter((account) => account.provider === resolvedProvider);
      const canonicalProviderAccountId = resolveProviderAccountIdentity(
        providerAccounts,
        selectedProviderAccountId,
      );
      const nextModelSelection: ModelSelection = {
        provider: resolvedProvider,
        model: resolvedModel,
        ...(canonicalProviderAccountId ? { providerAccountId: canonicalProviderAccountId } : {}),
      };

      // Sticky is thread-independent BY DESIGN, and that is the whole point
      // here: on /new there is no thread yet, only a draft. This used to begin
      // with `if (!input.activeThread) return;`, so a pick made on the
      // new-thread screen was discarded entirely — the picker's local display
      // moved to the chosen model while nothing was persisted, and send fell
      // back to the codex default. Production threads 67849623, 8e3f11f5 and
      // f0b8cf4a were all dispatched as gpt-5.6-sol after Claude was selected,
      // each with a single user_message and no assistant output. That read as
      // "Codex turns hang" and cost a day chasing credentials that were fine.
      input.setStickyComposerModelSelection(nextModelSelection);

      // The thread-scoped draft is the only part that genuinely needs a thread
      // to scope to.
      if (input.activeThread) {
        input.setComposerDraftModelSelection(
          scopeThreadRef(input.activeThread.environmentId, input.activeThread.id),
          nextModelSelection,
        );
      }
      input.scheduleComposerFocus();
    },
    [input, providerUsage.accounts],
  );

  return { onProviderModelSelect };
}
